import { saveCallbackStatus } from '@finpay-development/shared-components';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Comment } from '../../../patient/components/models/comment';
import { blankClient, blankClientBusinessRules, client, ClientBusinessRules } from '../../../shared/model/client';
import { ClientFacility, emptyClientFacility } from '../../../shared/model/client-facility';
import { ClientStatusCardViewModel } from '../../../shared/model/client-status-card';
import { ClientRiskAssessmentFields } from 'src/shared/model/client-risk-assessment-config';
import {
  getClientVobs
} from './implementation-clients-thunk';
import { UNKNOWN_SERVER_ERROR } from '../../../shared/state/saving-status';
import { emptyFacilitiesInfo, FacilitiesInfo } from '../../components/implementation-clients/details/models/implementation-facility';
import { emptyPayersInfo, PayersInfo } from '../../components/implementation-clients/details/models/payer-plan-info';
import { emptyTabComplete, TabComplete } from '../../components/implementation-clients/details/models/tabCompleteModel';
import {
  blankClientContactForm,
  blankClientEntityForm,
  blankClientForm,
  ClientContactForm,
  ClientEntityForm,
  ClientForm,
  emptyClientDeficiencyNotification,
  ClientDeficiencyNotification
} from '../../models/client';
import { ClientFilter, emptyClientFilter } from '../../models/client-filter';
import { ClientKPI, emptyClientKPI, FacilityKPI } from '../../models/kpi';
import { ClientReferralSource, FacilityLOCReferralSourceForm, SelectedFacilityReferralSource } from '../../models/referral-source';

import {
  deleteClientComment,
  getClient,
  getClients,
  saveClientBusinessRules,
} from './implementation-clients-thunk';
import {
  ClientPaymentTransactionFee, intialClientPaymentTransactionFee,
} from '../../models/payment-transaction-fee';
import {ClientVobStatusCardViewModel} from "../../../shared/model/client-vob-status-card";

interface InitialState {
  selectedFacility: ClientFacility,
  allClients: ClientStatusCardViewModel[],
  allClientVobs: ClientVobStatusCardViewModel[],
  allClientsMap: Map<number, ClientStatusCardViewModel>
  allClientsWithFacillities?: ClientStatusCardViewModel[],
  allClientsWithFacilitiesMap?: Map<number, ClientStatusCardViewModel>
  // client filtering
  filterFormValues: ClientFilter,
  filteredByIsActive: ClientStatusCardViewModel[],
  filteredByClosed: ClientStatusCardViewModel[],
  filteredByStripeSetupIncomplete: ClientStatusCardViewModel[],
  filteredByReadyForActivation: ClientStatusCardViewModel[],
  filteredBySetUpIncomplete: ClientStatusCardViewModel[],
  filteredByStripeActionNeeded: ClientStatusCardViewModel[],
  currentClientEntityDetails: ClientForm,
  currentClientBusinessRules: ClientBusinessRules,
  currentClient: ClientForm, // TODO(JB): think of renaming this type to not include word 'form'
  client: client; // full client object from server
  isActivating: boolean;

  editClientEntityForm: ClientEntityForm;
  editClientContactForm: ClientContactForm;

  isTabComplete: TabComplete;

  // common state for most/all slices
  apiStatus: saveCallbackStatus;
  errorMessage: string;
  isLoading: boolean;
  isLoadingComments: boolean;
  isLoadingClientKPIs: boolean;
  recentViewedClients:Array<number>;
  comments: Comment[];
  clientKPI: ClientKPI;
  facilityKPIs: FacilityKPI[];
  AAselectedFacilities: FacilitiesInfo;
  AAselectedPayers: PayersInfo;
  isLoadingLOCSpreadsheet: boolean;
  isLoadingPayorSpreadsheet: boolean;
  isLoadingRiskSetting: boolean,
  isLoadingPayerClaimRate: boolean,
  modalSaveStatus: saveCallbackStatus;
  clientReferralSources: ClientReferralSource[];
  facilityReferralSources: SelectedFacilityReferralSource[];
  isLoadingFacilityReferralSources: boolean,
  facilityReferralSourcesStatus: saveCallbackStatus,
  facilityLOCReferralSources: FacilityLOCReferralSourceForm[];
  isLoadingReferralSources: boolean,
  currentClientDeficiencyNotification: ClientDeficiencyNotification,
  isLoadingDeficiencyNotification: boolean,
  clientDeficiencyNotificationStatus: saveCallbackStatus,
  isLoadingClientRiskAssessmentConfigs: boolean,
  clientRiskAssessmentConfigs: ClientRiskAssessmentFields[],
  clientRiskAssessmentConfigLoadingStatus: saveCallbackStatus,
  deleteClientRiskAssessmentConfigStatus: saveCallbackStatus

  clientPaymentTransactionFee: {
    fees: ClientPaymentTransactionFee,
    clientId: number
  }
  isError: {
    client: boolean
  }
}

const initialState: InitialState = {
  selectedFacility: emptyClientFacility,
  allClients: [],
  allClientVobs: [],
  allClientsMap: new Map(),
  allClientsWithFacillities: [],
  allClientsWithFacilitiesMap: new Map(),
  filteredByIsActive: [],
  filteredByClosed: [],
  filteredByStripeSetupIncomplete: [],
  filteredByReadyForActivation: [],
  filteredBySetUpIncomplete: [],
  filteredByStripeActionNeeded: [],
  currentClientEntityDetails: blankClientForm,
  currentClientBusinessRules: blankClientBusinessRules,
  client: blankClient,
  isActivating: false,
  currentClient: blankClientForm,
  filterFormValues: emptyClientFilter(),

  isTabComplete: emptyTabComplete,

  apiStatus: saveCallbackStatus.none,
  errorMessage: "",
  isLoading: false,
  isLoadingComments: false,
  isLoadingClientKPIs: false,
  editClientEntityForm: blankClientEntityForm,
  editClientContactForm: blankClientContactForm,
  recentViewedClients:[],
  comments:[],
  clientKPI: emptyClientKPI,
  facilityKPIs: [],
  AAselectedFacilities: emptyFacilitiesInfo,
  AAselectedPayers: emptyPayersInfo,
  isLoadingLOCSpreadsheet: false,
  isLoadingPayorSpreadsheet: false,
  isLoadingRiskSetting: false,
  isLoadingPayerClaimRate: false,
  modalSaveStatus: saveCallbackStatus.none,
  clientReferralSources: [],
  facilityReferralSources: [],
  isLoadingFacilityReferralSources: false,
  facilityReferralSourcesStatus: saveCallbackStatus.none,
  facilityLOCReferralSources: [],
  isLoadingReferralSources: false,
  currentClientDeficiencyNotification: emptyClientDeficiencyNotification,
  isLoadingDeficiencyNotification: false,
  clientDeficiencyNotificationStatus: saveCallbackStatus.none,
  isLoadingClientRiskAssessmentConfigs: false,
  clientRiskAssessmentConfigs: [],
  clientRiskAssessmentConfigLoadingStatus: saveCallbackStatus.none,
  deleteClientRiskAssessmentConfigStatus: saveCallbackStatus.none,
  clientPaymentTransactionFee: {
    fees: intialClientPaymentTransactionFee,
    clientId: 0
  },
  isError: {
    client: false
  }
};

const implementationSpecialistClientSlice = createSlice({
  name: "implementationSpecialistClientContext",
  initialState,
  reducers: {
    setCurrentClient: (state, action) => {
      state.currentClient = action.payload;
    },
    clearStatus: (state) => {
      state.apiStatus = saveCallbackStatus.none;
      state.errorMessage = "";
    },
    setRulesEngineTabComplete: (state, action: PayloadAction<boolean>) => {
      state.isTabComplete = {
        ...state.isTabComplete,
        rulesEngine: action.payload
      }
    },
    clearComments: (state) => {
      state.comments = []
    },
    resetImplementationClientContext() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
        getClientVobs.fulfilled, (state, action) => {
          state.allClientVobs = action.payload;
        }
    );
    // getClients
    builder.addCase(
      getClients.fulfilled, (state, action) => {

        const allClientsMap = action.payload.allClients.reduce((clientsMap: Map<number, ClientStatusCardViewModel>, currClient: ClientStatusCardViewModel)=>{
          clientsMap.set(currClient.clientId, currClient)
          return clientsMap
        }, new Map())

        if (action.meta.arg.includeFacility) {
          state.allClientsWithFacillities = action.payload.allClients;
          state.allClientsWithFacilitiesMap = allClientsMap
        }
        state.allClients = action.payload.allClients;
        state.allClientsMap = allClientsMap;
        state.filteredByIsActive = action.payload.isActive;
        state.filteredByClosed = action.payload.closed;
        state.filteredByStripeSetupIncomplete = action.payload.stripeSetupIncomplete;
        state.filteredBySetUpIncomplete = action.payload.setupIncomplete;
        state.filteredByReadyForActivation = action.payload.readyForActivation;
        state.filteredByStripeActionNeeded = action.payload.stripeActionNeeded;
        state.isLoading = false;
          state.isError.client = false
      }
    );
    builder.addCase(getClients.pending, (state, action) => {
      state.isLoading = true;
      state.isError.client = false;
    });
    builder.addCase(getClients.rejected, (state, action) => {
      state.isLoading = false;
      state.isError.client = true;
    });

    // getClient
    builder.addCase(
      getClient.fulfilled, (state, action) => {
        state.currentClientEntityDetails = action.payload.clientEntityDetails;
        state.currentClientBusinessRules = action.payload.clientBusinessRules;
        state.client = action.payload.fullClient;
        state.apiStatus = saveCallbackStatus.none;
        state.isLoading = false;
        state.AAselectedFacilities = emptyFacilitiesInfo;
        state.AAselectedPayers = emptyPayersInfo;
      }
    )
    builder.addCase(getClient.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(getClient.rejected, (state, action) => {
      state.apiStatus = saveCallbackStatus.error;
      state.isLoading = false;
    });

    builder.addCase(saveClientBusinessRules.rejected, (state, action) => {
      state.apiStatus = saveCallbackStatus.error;
      state.errorMessage = action.error.message || UNKNOWN_SERVER_ERROR;
    });

    builder.addCase(saveClientBusinessRules.pending, (state) => {
      state.apiStatus = saveCallbackStatus.none;
    });

    builder.addCase(saveClientBusinessRules.fulfilled, (state, action) => {
      state.currentClientBusinessRules = action.payload.clientBusinessRules;
      state.client = action.payload.fullClient;
      state.apiStatus = saveCallbackStatus.success;
    });

    builder.addCase(deleteClientComment.pending, (state)=> {
      state.apiStatus = saveCallbackStatus.none;
    })

    builder.addCase(deleteClientComment.fulfilled, (state,action) => {
      state.apiStatus = saveCallbackStatus.success;
      const clientCommentId = action.payload.clientCommentsId
      state.comments= state.comments.filter(function( comments ) {
        return comments.clientCommentsId !== clientCommentId;
      });

    })

    builder.addCase(deleteClientComment.rejected,(state)=> {
      state.apiStatus = saveCallbackStatus.error;
    })
  },
});

export const {
  setCurrentClient,
  setRulesEngineTabComplete,
  resetImplementationClientContext,
} = implementationSpecialistClientSlice.actions;
export default implementationSpecialistClientSlice.reducer;
